import { YMMType, Model, Capabilities, Platform, YMMCookies, YmmResponseTypes } from './types';
import { CookieValueTypes, getCookie, setCookie } from 'cookies-next';
import dayjs from 'dayjs';
import { YMM_API_PATH } from '@/constants/ymm_api_path';

async function getYMM<RequestType extends YMMType>(
    type: RequestType,
    body: object,
): Promise<YmmResponseTypes[RequestType] | undefined> {
    const url = YMM_API_PATH + type;
    console.info(`Querying YMM : [${url}]`);
    const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            transactionid: `${new Date().getTime()}`,
            sourcename: 'SXMCOMDE',
            timestamp: new Date().toISOString(),
            'content-type': 'application/json',
        },
    });

    if (response.status < 300) {
        try {
            return await response.json();
        } catch (error) {
            const displayError = new Error(`Unable to parse ESB YMM response.\nURL: ${url},\nError: ${error}`);
            console.error(displayError);
        }
    }
}

export async function getYearsFrom(startYear: string): Promise<string[]> {
    const response = await getYMM('vehiclemodelyears', { startVehicleModelYear: startYear });
    return response?.vehicleModelYearList.sort().reverse() || [];
}

export async function getMakesForYear(year: string): Promise<string[]> {
    const response = await getYMM('vehiclemake', { vehicleModelYear: year });
    return response?.vehicleMakeList.sort() || [];
}

export async function getModelsForMake(year: string, make: string): Promise<Model[]> {
    const response = await getYMM('vehiclemodel', { vehicleModelYear: year, vehicleMake: make });
    return response?.vehicleModelList.sort() || [];
}

export async function getCapabilities(ymmComboId: string): Promise<Capabilities | undefined> {
    const fullCapabilities = await getYMM('vehiclecapabilities', { ymmComboId: ymmComboId });

    if (!fullCapabilities) {
        return undefined;
    }

    const capabilities: Capabilities = {
        localForecast: fullCapabilities.localForecast === 'Y',
        make: fullCapabilities.make || '',
        model: fullCapabilities.model || '',
        year: parseInt(`${fullCapabilities.modelYear}`, 10),
        navTraffic: fullCapabilities.navTraffic === 'Y',
        navWeather: fullCapabilities.navWeather === 'Y',
        siriusRadio: fullCapabilities.siriusRadio === 'Y',
        siriusxmRadio: fullCapabilities.siriusxmRadio === 'Y',
        siriusxmTraffic: fullCapabilities.siriusxmTraffic === 'Y',
        trafficPlus: fullCapabilities.trafficPlus === 'Y',
        travelLinkFuel: fullCapabilities.travelLinkFuel === 'Y',
        travelLinkMovies: fullCapabilities.travelLinkMovies === 'Y',
        travelLinkParking: fullCapabilities.travelLinkParking === 'Y',
        travelLinkSports: fullCapabilities.travelLinkSports === 'Y',
        travelLinkStocks: fullCapabilities.travelLinkStocks === 'Y',
        travelLinkWeather: fullCapabilities.travelLinkWeather === 'Y',
        xmRadio: fullCapabilities.xmRadio === 'Y',
        ymmComboId: fullCapabilities.ymmComboId || '',
        l360: fullCapabilities.TaData?.DeviceInfo.RadioModelTypeLegacyFeatures.l360 === 'Y',
        infotainment: [],
    };

    for (const capability in capabilities) {
        if (capabilities[capability as keyof Capabilities] === true) {
            capabilities.infotainment.push(capability);

            if (capabilities.infotainment.indexOf('Travel Link') === -1 && capability.indexOf('travelLink') >= 0) {
                capabilities.infotainment.push('Travel Link');
            }
        }
    }

    const hasNavTraffic = capabilities.infotainment.indexOf('navTraffic') > -1;
    const hasNavWeather = capabilities.infotainment.indexOf('navWeather') > -1;
    const hasLocalForecast = capabilities.infotainment.indexOf('localForecast') > -1;
    const hasTravelLink = capabilities.infotainment.indexOf('Travel Link') > -1;
    const hasTraffic = capabilities.infotainment.indexOf('siriusxmTraffic') > -1;
    const hasTrafficPlus = capabilities.infotainment.indexOf('trafficPlus') > -1;

    if (hasNavTraffic && hasNavWeather && hasTravelLink) {
        capabilities.infotainment.push('NavTraffic_NavWeather_TravelLink');
    } else if (hasNavTraffic && hasNavWeather) {
        capabilities.infotainment.push('NavTraffic_NavWeather');
    } else if (hasNavTraffic && hasLocalForecast) {
        capabilities.infotainment.push('NavTraffic_LocalForecast');
    } else if (hasNavTraffic && hasTravelLink) {
        capabilities.infotainment.push('NavTraffic_TravelLink');
    } else if (hasTraffic && hasTravelLink) {
        capabilities.infotainment.push('Traffic_TravelLink');
    } else if (hasTrafficPlus && hasTravelLink) {
        capabilities.infotainment.push('TrafficPlus_TravelLink');
    }

    return capabilities;
}

export function setYMMCookies(cookieValues: YMMCookies) {
    const domain = process.env.NODE_ENV === 'development' ? 'localhost' : '.siriusxm.com';
    const expires = dayjs().add(1, 'day').toDate();

    setCookie('sxm_radio', cookieValues.sxm_radio, {
        domain: domain,
        expires: expires,
        path: '/',
    });
    setCookie('sxm_platform', cookieValues.sxm_platform, {
        domain: domain,
        expires: expires,
        path: '/',
    });
    setCookie('inft_ymm', cookieValues.inft_ymm, {
        domain: domain,
        expires: expires,
        path: '/',
    });
    setCookie('ymm_bar', cookieValues.ymm_bar, {
        domain: domain,
        expires: expires,
        path: '/',
    });
    setCookie('roadblockbypass', cookieValues.roadblockbypass, {
        domain: domain,
        expires: expires,
        path: '/',
    });
}

export function getYMMCookies(): YMMCookies {
    return {
        sxm_radio: (getCookie('sxm_radio') as CookieValueTypes) || '',
        sxm_platform: (getCookie('sxm_platform') as Platform) || 'siriusxm',
        inft_ymm: (getCookie('inft_ymm') as CookieValueTypes) || '',
        ymm_bar: (getCookie('ymm_bar') as CookieValueTypes) || '',
        roadblockbypass: (getCookie('roadblockbypass') as CookieValueTypes) || '',
    };
}
